import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid py-4" }
const _hoisted_2 = { class: "d-flex justify-content-between align-items-center mb-4" }
const _hoisted_3 = {
  class: "nav nav-tabs",
  role: "tablist"
}
const _hoisted_4 = {
  class: "nav-item",
  role: "presentation"
}
const _hoisted_5 = {
  class: "nav-item",
  role: "presentation"
}
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "tab-content" }
const _hoisted_8 = { class: "tab-pane fade show active" }
const _hoisted_9 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4" }
const _hoisted_10 = { class: "d-flex align-items-center gap-2" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "pagination-mini" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = ["disabled", "onClick"]
const _hoisted_15 = ["disabled"]

import { ref, computed, onMounted, watch } from "vue"; 
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue"; 
import apiService from "@/services/apiService"; 
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";

const API_RESOURCE = "/common/module-variables";
const MODULE_FILTER = "people";
const pageTitle = "People";


// Tab management

export default /*@__PURE__*/_defineComponent({
  __name: 'People',
  setup(__props) {

const { canCreate } = usePermissions();

const activeTab = ref<"default" | "lookup">("default");
const moduleData = ref<any[]>([]);
const promptVariables = ref<any[]>([]);
const isLoading = ref(false);

// Computed title based on active tab
const currentTitle = computed(() =>
  activeTab.value === "default"
    ? "Default People Variables"
    : "By Users People Variables"
);

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});
const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});



// Table configuration with conditional actions
const tableConfig = computed(() => ({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Description", field: "description", sortable: true },
    { name: "Type", field: "variable_type_title", sortable: true },
    { name: "Data Items", field: "data_count", sortable: false },
    { name: "Created", field: "createdAt", sortable: true, type: "date" },
  ],
  data: processedData.value,
  actions: activeTab.value === "default" ? [true] : [false], // No actions for lookup
  loading: isLoading.value,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
}));

const changeRows = async () => {
  currentPage.value = 1;
  await fetchModules(1, tablePagination.value.per_page);
};

const handlePageChange = async (page:number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (page > tablePagination.value.total_pages) return;

  currentPage.value = page;
  await fetchModules(page, tablePagination.value.per_page);
};


// Process data to add computed fields
const processedData = computed(() => {
  return moduleData.value.map((item: any) => ({
    ...item,
    data_count: Array.isArray(item.data) ? item.data.length : 0,
    variable_type_title: item.variable_type_id?.title || "N/A",
  }));
});

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({
  module: MODULE_FILTER,
  title: "",
  description: "",
  variable_type_id: null as string | null,
  is_filterable: false,
  data: [] as string[],
  dataDisplay: "",
});

const editingItemData = ref({
  _id: "",
  module: MODULE_FILTER,
  title: "",
  description: "",
  variable_type_id: null as string | null,
  is_filterable: false,
  data: [] as string[],
  dataDisplay: "",
});

// --- Form Fields ---
const createFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
  },
  {
    key: "description",
    label: "Description",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter description",
  },
  {
    key: "variable_type_id",
    label: "Type",
    type: "select" as const,
    required: false,
    placeholder: "Select type",
    options: promptVariables.value,
  },
  {
    key: "is_filterable",
    label: "Filterable",
    type: "checkbox",
    required: false,
    readonly: isViewMode.value,
  },
  {
    key: "dataDisplay",
    label: "Data Items (comma-separated)",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter data items separated by commas",
  },
]);

const editFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
    readonly: isViewMode.value,
  },
  {
    key: "description",
    label: "Description",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter description",
    readonly: isViewMode.value,
  },
  {
    key: "variable_type_id",
    label: "Type",
    type: "select" as const,
    required: false,
    placeholder: "Select type",
    options: promptVariables.value,
    readonly: isViewMode.value,
  },
  {
    key: "is_filterable",
    label: "Filterable",
    type: "checkbox",
    required: false,
    readonly: isViewMode.value,
  },
  {
    key: "dataDisplay",
    label: "Data Items (comma-separated)",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter data items separated by commas",
    readonly: isViewMode.value,
  },
]);

// Tab management functions
const setActiveTab = (tab: "default" | "lookup") => {
  activeTab.value = tab;
  tablePagination.value.current_page = 1;
  fetchModules(1, tablePagination.value.per_page);
};

// Fetch modules based on active tab
const fetchModules = async (page = 1, limit = 10) => {
  isLoading.value = true;
  try {
    const workspaceParam = activeTab.value === "default" ? "false" : "true";
    const response = await apiService.get(`${API_RESOURCE}/workspace-scope`, {
      module: MODULE_FILTER,
      workspace: workspaceParam,
      page,
      limit,
    });

    // Handle different response structures
    if (Array.isArray(response)) {
      // Direct array response (non-paginated)
      moduleData.value = response;
      tablePagination.value.current_page = 1;
      tablePagination.value.total_pages = 1;
      tablePagination.value.total = response.length;
      tablePagination.value.has_prev = false;
      tablePagination.value.has_next = false;
    } else if (Array.isArray(response.data)) {
      // Paginated response with data property
      moduleData.value = response.data;
      
      // backend gives pagination object
      if (response.pagination) {
        tablePagination.value.current_page = response.pagination.current_page || page;
        tablePagination.value.total_pages = response.pagination.total_pages || 1;
        tablePagination.value.total = response.pagination.total || response.data.length;
        tablePagination.value.has_prev = (response.pagination.current_page || page) > 1;
        tablePagination.value.has_next =
          (response.pagination.current_page || page) < (response.pagination.total_pages || 1);
      } else {
        // Fallback if no pagination metadata
        tablePagination.value.current_page = page;
        tablePagination.value.total_pages = 1;
        tablePagination.value.total = response.data.length;
        tablePagination.value.has_prev = false;
        tablePagination.value.has_next = false;
      }
    } else {
      moduleData.value = [];
      tablePagination.value.current_page = 1;
      tablePagination.value.total_pages = 1;
      tablePagination.value.total = 0;
      tablePagination.value.has_prev = false;
      tablePagination.value.has_next = false;
    }
  } catch (error) {
    console.error("Error fetching modules:", error);
    moduleData.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Fetch variable types for the Type selector
const loadPromptVariables = async () => {
  try {
    const response = await apiService.get("/common/variable-types");
    if (Array.isArray(response)) {
      promptVariables.value = response;
    } else if (response && response.data && Array.isArray(response.data)) {
      promptVariables.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching variable types:", error);
  }
};

// Modal management
const openCreateModal = () => {
  isCreateModalOpen.value = true;
};

// --- Data conversion watchers ---
watch(
  () => newItemData.value.dataDisplay,
  (newVal) => {
    if (newVal) {
      newItemData.value.data = newVal
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    } else {
      newItemData.value.data = [];
    }
  }
);

watch(
  () => editingItemData.value.dataDisplay,
  (newVal) => {
    if (newVal) {
      editingItemData.value.data = newVal
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    } else {
      editingItemData.value.data = [];
    }
  }
);

// --- Validation ---
const isCreateFormValid = computed(() => !newItemData.value.title.trim());

const isEditFormValid = computed(() => !editingItemData.value.title?.trim());

// --- Handlers ---
const handleEdit = async (row: any) => {
  // Only allow edit for default tab
  if (activeTab.value !== "default") return;

  isViewMode.value = false;
  const dataArray = Array.isArray(row.data) ? [...row.data] : [];
  
  // Extract ID from nested variable_type_id object if necessary
  const typeId = row.variable_type_id?._id || row.variable_type_id || null;

  editingItemData.value = {
    ...row,
    variable_type_id: typeId,
    data: dataArray,
    dataDisplay: dataArray.join(", "),
  };
  isEditModalOpen.value = true;
};

const handleView = async (row: any) => {
  isViewMode.value = true;
  const dataArray = Array.isArray(row.data) ? [...row.data] : [];
  
  // Extract ID from nested variable_type_id object if necessary
  const typeId = row.variable_type_id?._id || row.variable_type_id || null;

  editingItemData.value = {
    ...row,
    variable_type_id: typeId,
    data: dataArray,
    dataDisplay: dataArray.join(", "),
  };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  // Only allow delete for default tab
  if (activeTab.value !== "default") return;

  if (confirm("Are you sure you want to delete this item?")) {
    try {
      await apiService.delete(API_RESOURCE, id);
      await fetchModules(tablePagination.value.current_page, tablePagination.value.per_page);
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;

  const { dataDisplay, ...rest } = newItemData.value;
  const payload = {
    ...rest,
    data: rest.data.filter((item) => item.trim().length > 0),
  };

  // Use direct API call since we need to use the base endpoint for creation
  try {
    await apiService.post(API_RESOURCE, payload);
    await fetchModules(1, tablePagination.value.per_page);
    closeCreateModal();
  } catch (error) {
    console.error("Error creating item:", error);
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;

  const { _id, dataDisplay, ...rest } = editingItemData.value;
  const payload = {
    ...rest,
    data: rest.data.filter((item) => item.trim().length > 0),
  };

  // Use direct API call since we need to use the base endpoint for updates
  try {
    await apiService.patch(API_RESOURCE, id, payload);
    await fetchModules(tablePagination.value.current_page, tablePagination.value.per_page);
    closeEditModal();
  } catch (error) {
    console.error("Error updating item:", error);
  }
};

const onCreateModalShown = () => {
  // Focus on title field when modal opens
};

const onEditModalShown = () => {
  // Focus on title field when modal opens
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  isViewMode.value = false;
  newItemData.value = {
    module: MODULE_FILTER,
    title: "",
    description: "",
    is_filterable: false,
    data: [],
    dataDisplay: "",
    variable_type_id: null,
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = {
    _id: "",
    module: MODULE_FILTER,
    title: "",
    description: "",
    is_filterable: false,
    data: [],
    dataDisplay: "",
    variable_type_id: null,
  };
};

// --- Load data ---
onMounted(async () => {
  await Promise.all([
    fetchModules(tablePagination.value.current_page, tablePagination.value.per_page),
    loadPromptVariables()
  ]);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("ul", _hoisted_3, [
          _createElementVNode("li", _hoisted_4, [
            _createElementVNode("button", {
              class: _normalizeClass(["nav-link", { active: activeTab.value === 'default' }]),
              onClick: _cache[0] || (_cache[0] = ($event: any) => (setActiveTab('default'))),
              type: "button"
            }, " Default ", 2)
          ]),
          _createElementVNode("li", _hoisted_5, [
            _createElementVNode("button", {
              class: _normalizeClass(["nav-link", { active: activeTab.value === 'lookup' }]),
              onClick: _cache[1] || (_cache[1] = ($event: any) => (setActiveTab('lookup'))),
              type: "button"
            }, " By Users ", 2)
          ])
        ]),
        (activeTab.value === 'default')
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "btn btn-primary",
              onClick: openCreateModal,
              disabled: !_unref(canCreate)('main')
            }, [...(_cache[7] || (_cache[7] = [
              _createElementVNode("i", { class: "fa fa-plus" }, null, -1),
              _createTextVNode(" Create New ", -1)
            ]))], 8, _hoisted_6))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(CommonTable2, {
            tableObj: tableConfig.value,
            title: currentTitle.value,
            permissionKey: "main",
            onEdit: handleEdit,
            onDelete: handleDelete,
            onView: handleView
          }, null, 8, ["tableObj", "title"])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", _hoisted_10, [
        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
        _withDirectives(_createElementVNode("select", {
          class: "form-select form-select-sm rows-select",
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((tablePagination.value.per_page) = $event)),
          onChange: changeRows
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
            return _createElementVNode("option", {
              key: n,
              value: n
            }, _toDisplayString(n), 9, _hoisted_11)
          }), 64))
        ], 544), [
          [_vModelSelect, tablePagination.value.per_page]
        ])
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("button", {
          class: "page-btn prev_btn",
          disabled: !tablePagination.value.has_prev,
          onClick: _cache[3] || (_cache[3] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
        }, " « ", 8, _hoisted_13),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
          return (_openBlock(), _createElementBlock("button", {
            key: index,
            class: _normalizeClass(["page-btn", {
        'page-active': p === tablePagination.value.current_page,
        'disabled-dots': p === '...',
      }]),
            disabled: p === '...',
            onClick: ($event: any) => (p !== '...' && typeof(p) === 'number'? handlePageChange(p) : '...')
          }, _toDisplayString(p), 11, _hoisted_14))
        }), 128)),
        _createElementVNode("button", {
          class: "page-btn next_btn",
          disabled: !tablePagination.value.has_next,
          onClick: _cache[4] || (_cache[4] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
        }, " » ", 8, _hoisted_15)
      ])
    ]),
    _createVNode(BaseModal, {
      id: "createNew",
      title: `Create ${pageTitle}`,
      visible: isCreateModalOpen.value,
      onClose: closeCreateModal,
      onSave: handleSaveCreate,
      onShown: onCreateModalShown,
      "save-button-disabled": isCreateFormValid.value,
      "dialog-class": "modal-lg"
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: createFormFields.value,
          modelValue: newItemData.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((newItemData).value = $event)),
          onSubmit: handleSaveCreate,
          ref: "createFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "save-button-disabled"]),
    _createVNode(BaseModal, {
      id: "editModal",
      title: `${isViewMode.value ? 'View' : 'Edit'} ${pageTitle}`,
      visible: isEditModalOpen.value,
      onClose: closeEditModal,
      onSave: handleSaveEdit,
      onShown: onEditModalShown,
      "show-save-button": !isViewMode.value,
      "save-button-disabled": isEditFormValid.value,
      "dialog-class": "modal-lg"
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: editFormFields.value,
          modelValue: editingItemData.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editingItemData).value = $event)),
          onSubmit: handleSaveEdit,
          ref: "editFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "show-save-button", "save-button-disabled"])
  ], 64))
}
}

})